import { leaseRepository } from '~/repositories/leaseRepository';
import { propertiesRepository } from '~/repositories/propertiesRepository';
import { PropertyManagersRepository } from '~/repositories/propertyManagersRepository';
import { tenantRepository } from '~/repositories/tenantRepository';
import { unitRepository } from '~/repositories/unitRepository';
import { uploadSessionRepository } from '~/repositories/uploadSessionRepository';
import { validateId } from '~/repositories/utils';
import { maintenanceServiceUrl } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type { UploadFileOptions } from '~/repositories/uploadSessionRepository';
import type {
  AddWorkSessionDto,
  CreateWorkOrderDraftBillRequestDto,
  CreateWorkOrderRequestDto,
  HistoryPageDto,
  HydratedLeaseDto,
  HydratedPropertyDto,
  HydratedTenantDto,
  HydratedUnitDto,
  HydratedWorkOrderBillDto,
  HydratedWorkOrderListDto,
  SendWorkOrderVendorNotificationDto,
  UpdateWorkOrderLeaseDto,
  UpdateWorkOrderMaintenanceRequestDto,
  UpdateWorkOrderRequestDto,
  UpdateWorkOrderStatusDto,
  UpdateWorkOrderVendorDto,
  UpdateWorkSessionDto,
  WorkSessionDto,
} from '~/swagger/maintenance/Api';
import type { HydratedWorkOrderDto, WorkOrderStatus as ApiWorkOrderStatus } from '~/swagger/portal/Api';

const propertyManagersRepository = new PropertyManagersRepository();

declare module '~/swagger/portal/Api' {
  interface HydratedLeaseTenantDto {
    tenantId?: string;
  }

  interface HydratedWorkOrderDto {
    sessions?: WorkSessionDto[];
    assignedOn?: string;
    closedOn?: string;
    recurringWorkOrderId?: string;
  }
}

/**
 * Work-order detail as this app consumes it. The response comes from the Maintenance
 * service, so `bills` carries Maintenance's shape rather than the stale portal one the
 * rest of `HydratedWorkOrderDto` is still typed against.
 */
export type HydratedWorkOrder = Omit<HydratedWorkOrderDto, 'bills'> & {
  bills?: HydratedWorkOrderBillDto[];
};

export type WorkOrderStatus = `${ApiWorkOrderStatus}`;

export type WorkOrdersFilter = {
  page?: number;
  pageSize?: number;
  portfolioIds?: string[];
  propertyIds?: string[];
  unitIds?: string[];
  leaseIds?: string[];
  vendorIds?: string[];
  recurringWorkOrderIds?: string[];
  status?: WorkOrderStatus | WorkOrderStatus[];
  searchText?: string;
  active?: boolean;
  isScheduled?: boolean;
  scheduledTime?: { start?: string; end?: string };
  closedAfter?: string;
};

export type WorkOrdersScheduleDto = Omit<HydratedWorkOrdersList, 'currentPage' | 'totalPages' | 'pageSize' | 'totalCount'>;

/**
 * Client-side hydrated work-order list. The Maintenance service now returns
 * IDs-only pages; the referenced entities are fetched from their owning
 * services here so the UI keeps the same shape it had before.
 */
export type HydratedWorkOrdersList = {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  items: HydratedWorkOrderListDto[];
  properties: HydratedPropertyDto[];
  units: HydratedUnitDto[];
  leases: HydratedLeaseDto[];
  tenants: HydratedTenantDto[];
};

type WorkOrdersPage = {
  items: HydratedWorkOrderListDto[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
};

const uniqueIds = (ids: (string | null | undefined)[]): string[] => [...new Set(ids.filter((id): id is string => !!id))];

const fetchByIds = <T>(ids: string[], fetch: (ids: string[]) => Promise<T[]>): Promise<T[]> =>
  ids.length ? fetch(ids) : Promise.resolve<T[]>([]);

const dedupeById = <T extends { id: string }>(rows: T[]): T[] => [...new Map(rows.map((row) => [row.id, row])).values()];

export const WORK_ORDER_BASE_URL = `${maintenanceServiceUrl()}/company-portal/work-orders`;
export class WorkOrdersRepository extends BaseRestRepository {
  public async getWorkOrders(filter: WorkOrdersFilter): Promise<HydratedWorkOrdersList> {
    const url = urlWithQuery(WORK_ORDER_BASE_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.hydrateWorkOrders((await this.getJsonResponse(response)) as WorkOrdersPage);
  }

  public async searchWorkOrders(filter: WorkOrdersFilter): Promise<{ items: HydratedWorkOrderListDto[]; totalPages: number }> {
    const url = urlWithQuery(WORK_ORDER_BASE_URL, filter);
    const response = await this.fetchWithAuth(url);
    const page = (await this.getJsonResponse(response)) as WorkOrdersPage;
    return { items: page.items ?? [], totalPages: page.totalPages };
  }

  public async getAllWorkOrders(filter: WorkOrdersFilter): Promise<HydratedWorkOrdersList> {
    const url = urlWithQuery(`${WORK_ORDER_BASE_URL}/all`, filter);
    const response = await this.fetchWithAuth(url);
    return this.hydrateWorkOrders((await this.getJsonResponse(response)) as WorkOrdersPage, { withLeases: false, fromLocation: true });
  }

  private async hydrateEntities(
    items: HydratedWorkOrderListDto[],
    options: { withLeases?: boolean; fromLocation?: boolean } = {}
  ): Promise<Pick<HydratedWorkOrdersList, 'properties' | 'units' | 'leases' | 'tenants'>> {
    let properties: HydratedPropertyDto[];
    let units: HydratedUnitDto[];
    if (options.fromLocation) {
      properties = dedupeById(
        items
          .filter((item) => item.propertyId)
          .map((item) => ({
            id: item.propertyId,
            imageId: item.location?.propertyImageId ?? undefined,
            name: item.location?.propertyName ?? undefined,
            address: item.location?.address ?? undefined,
          }))
      ) as unknown as HydratedPropertyDto[];
      units = dedupeById(
        items
          .filter((item): item is HydratedWorkOrderListDto & { unitId: string } => !!item.unitId)
          .map((item) => ({ id: item.unitId, name: item.location?.unitName ?? undefined }))
      ) as unknown as HydratedUnitDto[];
    } else {
      const propertyIds = uniqueIds(items.map((item) => item.propertyId));
      const unitIds = uniqueIds(items.map((item) => item.unitId));
      const [fetchedProperties, fetchedUnits] = await Promise.all([
        fetchByIds(propertyIds, (ids) => propertiesRepository.batchGetPropertyByIds(ids)),
        fetchByIds(unitIds, (ids) => unitRepository.batchGetUnitsByIds(ids)),
      ]);
      properties = fetchedProperties as unknown as HydratedPropertyDto[];
      units = fetchedUnits as unknown as HydratedUnitDto[];
    }

    if (options.withLeases === false) {
      return { properties, units, leases: [], tenants: [] };
    }

    const leaseIds = uniqueIds(items.map((item) => item.leaseId));
    const serviceLeases = await fetchByIds(leaseIds, (ids) => leaseRepository.batchGetLeasesByIds(ids));

    // The leasing service exposes lease tenants as { tenant: TenantDto }; the UI expects
    // the Maintenance sidecar shape { tenantId }, so remap that one field here.
    const leases = serviceLeases.map((lease) => ({
      ...lease,
      tenants: (lease.tenants ?? []).map((leaseTenant) => ({
        tenantId: leaseTenant.tenant.id,
        moveIn: leaseTenant.moveIn,
        moveOut: leaseTenant.moveOut,
      })),
    })) as unknown as HydratedLeaseDto[];

    const tenantIds = uniqueIds(leases.flatMap((lease) => (lease.tenants ?? []).map((tenant) => tenant.tenantId)));
    const serviceTenants = tenantIds.length ? await tenantRepository.getTenantsByIds({ tenantIds }) : [];

    return {
      properties,
      units,
      leases,
      tenants: serviceTenants as unknown as HydratedTenantDto[],
    };
  }

  private async hydrateWorkOrders(
    page: WorkOrdersPage,
    options?: { withLeases?: boolean; fromLocation?: boolean }
  ): Promise<HydratedWorkOrdersList> {
    const items = page.items ?? [];
    return {
      currentPage: page.page,
      totalPages: page.totalPages,
      pageSize: page.pageSize,
      totalCount: page.totalCount,
      items,
      ...(await this.hydrateEntities(items, options)),
    };
  }

  public async getWorkOrdersForScheduling(filter: WorkOrdersFilter): Promise<WorkOrdersScheduleDto> {
    const url = urlWithQuery(`${WORK_ORDER_BASE_URL}/schedule`, filter);
    const response = await this.fetchWithAuth(url);
    const { items = [] } = (await this.getJsonResponse(response)) as { items: HydratedWorkOrderListDto[] };
    return { items, ...(await this.hydrateEntities(items, { withLeases: false, fromLocation: true })) };
  }

  // The Maintenance service returns IDs-only detail responses; fetch the referenced
  // entities from their owning services so the detail screens keep their shape.
  private async hydrateWorkOrderDetail(raw: any): Promise<HydratedWorkOrder> {
    // The linked maintenance request is fetched lazily by the component that renders it
    // (it carries maintenanceRequestId), so it is not fetched here.
    const [property, unit, lease, assignedPropertyManager] = await Promise.all([
      raw.propertyId ? propertiesRepository.getProperty(raw.propertyId).catch(() => undefined) : undefined,
      raw.unitId ? unitRepository.batchGetUnitsByIds([raw.unitId]).then((units) => units[0]) : undefined,
      raw.leaseId ? leaseRepository.batchGetLeasesByIds([raw.leaseId]).then((leases) => leases[0]) : undefined,
      raw.assignedPropertyManagerId
        ? propertyManagersRepository.getPropertyManagerById(raw.assignedPropertyManagerId).catch(() => undefined)
        : undefined,
    ]);
    // The vendor now arrives embedded on the detail response.
    const vendor = raw.vendor ?? undefined;

    const tenantIds = uniqueIds((lease?.tenants ?? []).map((leaseTenant: any) => leaseTenant.tenant?.id ?? leaseTenant.tenantId));
    const tenants = tenantIds.length ? await tenantRepository.getTenantsByIds({ tenantIds }) : [];

    return {
      ...raw,
      property,
      // property.owners is the nested { owner, ownershipPercentage } shape; the detail expects flat owners.
      owners: (property?.owners ?? []).map((propertyOwner: any) => ({
        ...propertyOwner.owner,
        ownershipPercentage: propertyOwner.ownershipPercentage,
      })),
      unit,
      lease,
      vendor,
      tenants,
      assignedPropertyManager,
    } as HydratedWorkOrder;
  }

  public async getWorkOrder(workOrderId: string): Promise<HydratedWorkOrder> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}`;
    const response = await this.fetchWithAuth(url);
    return this.hydrateWorkOrderDetail(await this.getJsonResponse(response));
  }

  public async getWorkOrderSummary(workOrderId: string): Promise<HydratedWorkOrder> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}`;
    const response = await this.fetchWithAuth(url);
    const raw = await this.getJsonResponse(response);
    const property = raw.propertyId ? await propertiesRepository.getProperty(raw.propertyId).catch(() => undefined) : undefined;
    return {
      ...raw,
      property,
      owners: (property?.owners ?? []).map((propertyOwner: any) => ({
        ...propertyOwner.owner,
        ownershipPercentage: propertyOwner.ownershipPercentage,
      })),
      vendor: raw.vendor ?? undefined,
    } as HydratedWorkOrder;
  }

  public async addWorkOrder(payload: CreateWorkOrderRequestDto): Promise<HydratedWorkOrder> {
    const url = WORK_ORDER_BASE_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.hydrateWorkOrderDetail(await this.getJsonResponse(response));
  }

  public async updateMaintenanceRequest(workOrderId: string, payload: UpdateWorkOrderMaintenanceRequestDto): Promise<HydratedWorkOrder> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/maintenance-request`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.hydrateWorkOrderDetail(await this.getJsonResponse(response));
  }

  public async deleteWorkOrder(workOrderId: string): Promise<void> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async updateWorkOrder(workOrderId: string, payload: UpdateWorkOrderRequestDto): Promise<HydratedWorkOrder> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.hydrateWorkOrderDetail(await this.getJsonResponse(response));
  }

  public async updateWorkOrderStatus(workOrderId: string, payload: UpdateWorkOrderStatusDto): Promise<HydratedWorkOrder> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/status`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.hydrateWorkOrderDetail(await this.getJsonResponse(response));
  }

  public async updateVendor(workOrderId: string, payload: UpdateWorkOrderVendorDto): Promise<HydratedWorkOrder> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/vendor`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.hydrateWorkOrderDetail(await this.getJsonResponse(response));
  }

  public async updateLease(workOrderId: string, payload: UpdateWorkOrderLeaseDto): Promise<HydratedWorkOrder> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/lease`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.hydrateWorkOrderDetail(await this.getJsonResponse(response));
  }

  public async addWorkOrderSession(workOrderId: string, payload: AddWorkSessionDto): Promise<HydratedWorkOrder> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/sessions`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.hydrateWorkOrderDetail(await this.getJsonResponse(response));
  }

  public async updateWorkOrderSession(workOrderId: string, sessionId: string, payload: UpdateWorkSessionDto): Promise<HydratedWorkOrder> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/sessions/${sessionId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.hydrateWorkOrderDetail(await this.getJsonResponse(response));
  }

  public async deleteWorkOrderSession(workOrderId: string, sessionId: string): Promise<HydratedWorkOrder> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/sessions/${sessionId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
    return this.hydrateWorkOrderDetail(await this.getJsonResponse(response));
  }

  public async addDraftBill(workOrderId: string, payload: CreateWorkOrderDraftBillRequestDto): Promise<HydratedWorkOrderBillDto> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/draft-bills`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async updateDraftBill(
    workOrderId: string,
    billId: string,
    payload: CreateWorkOrderDraftBillRequestDto
  ): Promise<HydratedWorkOrderBillDto> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/draft-bills/${billId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteDraftBill(workOrderId: string, billId: string): Promise<void> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/draft-bills/${billId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async convertDraftBill(workOrderId: string, billId: string): Promise<void> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/draft-bills/${billId}/convert`;
    await this.fetchWithAuth(url, {
      method: 'POST',
    });
  }

  public async uploadWorkOrderFiles(workOrderId: string, files: File[], opts?: UploadFileOptions, description?: string): Promise<string[]> {
    validateId(workOrderId, 'WorkOrder');
    if (files.length === 0) return [];

    const uploadSessionIds = await uploadSessionRepository.uploadFiles(
      `${WORK_ORDER_BASE_URL}/${workOrderId}/files/upload-sessions`,
      files,
      opts
    );

    await this.fetchWithAuth(`${WORK_ORDER_BASE_URL}/${workOrderId}/files`, {
      method: 'POST',
      body: JSON.stringify({
        files: uploadSessionIds.map((uploadSessionId) => (description ? { uploadSessionId, description } : { uploadSessionId })),
      }),
    });

    return uploadSessionIds;
  }

  public async deleteWorkOrderFile(workOrderId: string, fileId: string): Promise<void> {
    validateId(workOrderId, 'WorkOrder');
    validateId(fileId, 'File');
    await this.fetchWithAuth(`${WORK_ORDER_BASE_URL}/${workOrderId}/files/${fileId}`, { method: 'DELETE' });
  }

  public async uploadDraftBillFiles(workOrderId: string, billId: string, files: File[], opts?: UploadFileOptions): Promise<string[]> {
    validateId(workOrderId, 'WorkOrder');
    validateId(billId, 'Bill');
    if (files.length === 0) return [];
    return uploadSessionRepository.uploadFiles(
      `${WORK_ORDER_BASE_URL}/${workOrderId}/draft-bills/${billId}/files/upload-sessions`,
      files,
      opts
    );
  }

  public async addDraftBillFiles(
    workOrderId: string,
    billId: string,
    files: { uploadSessionId: string; description?: string }[]
  ): Promise<HydratedWorkOrderBillDto> {
    validateId(workOrderId, 'WorkOrder');
    validateId(billId, 'Bill');
    const response = await this.fetchWithAuth(`${WORK_ORDER_BASE_URL}/${workOrderId}/draft-bills/${billId}/files`, {
      method: 'POST',
      body: JSON.stringify({ files }),
    });
    return this.getJsonResponse(response);
  }

  public async deleteBillFile(workOrderId: string, billId: string, fieldId: string): Promise<void> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/draft-bills/${billId}/files/${fieldId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async uploadWorkOrderNotificationAttachments(workOrderId: string, files: File[], opts?: UploadFileOptions): Promise<string[]> {
    validateId(workOrderId, 'WorkOrder');
    const sessionUrl = `${WORK_ORDER_BASE_URL}/${workOrderId}/notification/upload-sessions`;
    return await uploadSessionRepository.uploadFiles(sessionUrl, files, opts);
  }

  public async createWorkOrderNotification(workOrderId: string, payload: SendWorkOrderVendorNotificationDto): Promise<void> {
    const url = `${WORK_ORDER_BASE_URL}/${workOrderId}/notification/vendor`;
    await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  public async getUserWorkOrders(filter: WorkOrdersFilter): Promise<HydratedWorkOrdersList> {
    const url = urlWithQuery(`${WORK_ORDER_BASE_URL}/mine`, filter);
    const response = await this.fetchWithAuth(url);
    const { items = [], totalCount } = (await this.getJsonResponse(response)) as { items: HydratedWorkOrderListDto[]; totalCount: number };
    return {
      currentPage: 1,
      totalPages: 1,
      pageSize: items.length,
      totalCount: totalCount ?? items.length,
      items,
      ...(await this.hydrateEntities(items)),
    };
  }

  public async getWorkOrderHistory(workOrderId: string, filter?: { page?: number; pageSize?: number }): Promise<HistoryPageDto> {
    const url = urlWithQuery(`${WORK_ORDER_BASE_URL}/${workOrderId}/history`, filter ?? {});
    const response = await this.fetchWithAuth(url);
    const page = (await this.getJsonResponse(response)) as Pick<HistoryPageDto, 'items'> & {
      page: number;
      totalPages: number;
      pageSize: number;
      totalCount: number;
    };
    return { currentPage: page.page, totalPages: page.totalPages, pageSize: page.pageSize, totalCount: page.totalCount, items: page.items };
  }
}
